<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    :title="`${$i18n.t('dialog.manageDataSourceAttributes')} - ${editingDataSource.verbose_name}`"
    min-body-height="55vh"
    dialog-width="70%"
    header-shadow
    disagree-text="dialog.disagreeClose"
    :is-agree-visible="false"
    @change="$root.$emit('attributesChanged')"
  >
    <template #body>
      <template v-if="isLoaded">
        <v-col cols="12">
          <v-row no-gutters justify="center" align="center" class="py-3 mx-10">
            <v-col class="columnTitle" v-for="columnTitle of columnsTitles" :key="columnTitle.name">
              <span>
                {{ $i18n.t(columnTitle.translatePath) }}
              </span>
              <v-tooltip bottom color="font" v-if="columnTitle.info">
                <template #activator="{ on }">
                  <v-icon v-on="on" small style="padding-bottom:3px" color="#0091EA">
                    mdi-information
                  </v-icon>
                </template>
                <span>
                  {{ $i18n.t(`${columnTitle.translatePath}Info`) }}
                </span>
              </v-tooltip>
            </v-col>
          </v-row>
          <v-radio-group v-model="descAttribute" hide-details dense class="mt-0 pt-0">
            <span v-for="attribute of filteredAttributes" :key="attribute.name">
              <v-row no-gutters align="baseline" class="pt-4 mx-10 border-top">
                <v-col class="columnElementText">
                  <data-input
                    v-if="!attribute.predefined"
                    v-model="attribute.verbose_name"
                    @change="setAttributeHandler($event, attribute, 'verbose_name')"
                    class="px-5"
                    :data-type="dataTypeText"
                  >
                  </data-input>
                  <span v-else>
                    {{ attribute.verbose_name }}
                  </span>
                </v-col>
                <v-col class="columnElementText">
                  {{ attribute.name }}
                </v-col>
                <v-col class="columnElementText">
                  {{
                    dataTypes.length > 0
                      ? dataTypes.find(dT => dT.name === attribute.data_type.name).verbose_name
                      : attribute.data_type.name
                  }}
                </v-col>
                <v-col class="d-flex justify-center">
                  <v-radio :value="attribute.name" />
                </v-col>
                <v-col style="overflow: hidden">
                  <v-tooltip bottom color="font" :disabled="!attribute.read_only">
                    <template #activator="{ on }">
                      <span v-on="on">
                        <data-input
                          :disabled="attribute.read_only"
                          v-model="attribute.dict_id"
                          :menu-props="{ offsetY: true, minWidth: '300px', maxWidth: '300px' }"
                          :items="Object.values(dictionaries).filter(d => d.data_type === attribute.data_type.name)"
                          item-value="id"
                          item-text="name"
                          clearable
                          @change="setAttributeHandler($event, attribute, 'dict_id')"
                          class="px-5"
                          :data-type="dataType"
                        >
                        </data-input>
                      </span>
                    </template>
                    {{ $i18n.t('dialog.body.readyOnlyDict') }}
                  </v-tooltip>
                </v-col>
                <v-col style="overflow: hidden">
                  <data-input
                    :menu-props="{ offsetY: true, minWidth: '300px', maxWidth: '300px' }"
                    :items="formattedMeasurementUnits"
                    item-value="id"
                    item-text="text"
                    class="px-5"
                    clearable
                    @change="setAttributeHandler($event, attribute, 'measurement_unit_id')"
                    v-model="attribute.measurement_unit"
                    :data-type="dataType"
                    disabled-sorting
                  >
                  </data-input>
                </v-col>
                <v-col>
                  <v-tooltip bottom color="font" :disabled="!attribute.predefined">
                    <template #activator="{ on }">
                      <span v-on="on" class="d-flex justify-center">
                        <v-icon
                          :disabled="attribute.predefined"
                          color="error"
                          @click="confirmAttributeDelete(attribute)"
                        >
                          mdi-close
                        </v-icon>
                      </span>
                    </template>
                    {{ $i18n.t('dialog.deleteAttributesInfo') }}
                  </v-tooltip>
                </v-col>
              </v-row>
            </span>
            <v-form v-model="isNewAttributeValid" ref="form">
              <v-row no-gutters justify="center" align="baseline" class="pt-4 mx-10 border-top">
                <v-col>
                  <data-input
                    :data-type="dataTypeText"
                    :rules="[v => !!v || $i18n.t('rules.required')]"
                    v-model="newAttribute.verbose_name"
                    class="px-5 ma-0"
                  />
                </v-col>
                <v-col>
                  <data-input
                    :data-type="dataTypeText"
                    :rules="[v => /^[a-zA-Z]+[\w\d]*$/.test(v || '') || $i18n.t('rules.incorrectValue')]"
                    v-model="newAttribute.name"
                    class="px-5 py-2 ma-0"
                  />
                </v-col>
                <v-col>
                  <data-input
                    :menu-props="{ offsetY: true, minWidth: '200px' }"
                    v-model="newAttribute.data_type"
                    :items="excludedDataTypes"
                    item-text="verbose_name"
                    item-value="name"
                    @change="newAttribute.dict_id = undefined"
                    class="px-5"
                    :data-type="dataType"
                  >
                  </data-input>
                </v-col>
                <v-col class="d-flex justify-center">
                  <v-tooltip top color="font">
                    <template v-slot:activator="{ on }">
                      <span v-on="on">
                        <v-radio disabled />
                      </span>
                    </template>
                    <span>{{ $i18n.t('dialog.descriptionNameTooltip') }}</span>
                  </v-tooltip>
                </v-col>
                <v-col>
                  <v-tooltip bottom color="font" :disabled="!!newAttribute.data_type">
                    <template #activator="{ on }">
                      <span v-on="on">
                        <data-input
                          :menu-props="{ offsetY: true, minWidth: '300px', maxWidth: '300px' }"
                          v-model="newAttribute.dict_id"
                          :items="Object.values(dictionaries).filter(d => d.data_type === newAttribute.data_type)"
                          item-value="id"
                          item-text="name"
                          clearable
                          :disabled="!newAttribute.data_type"
                          class="px-5"
                          :data-type="dataType"
                        >
                        </data-input>
                      </span>
                    </template>
                    {{ $i18n.t('dialog.body.dataTypeDict') }}
                  </v-tooltip>
                </v-col>
                <v-col>
                  <data-input
                    :menu-props="{ offsetY: true, minWidth: '300px', maxWidth: '300px' }"
                    :items="formattedMeasurementUnits"
                    item-text="text"
                    item-value="id"
                    v-model="newAttribute.measurement_unit_id"
                    clearable
                    class="px-5"
                    :data-type="dataType"
                    disabled-sorting
                  >
                  </data-input>
                </v-col>
                <v-col class="d-flex justify-center">
                  <v-icon
                    :style="`cursor: ${isNewAttributeValid ? 'pointer' : 'default'}`"
                    :color="isNewAttributeValid ? 'success' : 'grey'"
                    @click="handleAttributeAdd"
                  >
                    mdi-check
                  </v-icon>
                </v-col>
              </v-row>
            </v-form>
          </v-radio-group>
          <main-dialog
            v-if="isDeleteAttributeLoaded"
            :is-visible.sync="isDeleteAttributeVisible"
            :title="$i18n.t('dialog.deleteAttribute')"
            :body="$i18n.t('dialog.confirmAttributeDelete', { name: attributeToDelete.name })"
            agree-text="dialog.agreeDelete"
            @agree="handleAttributeDelete()"
          >
          </main-dialog
        ></v-col>
      </template>
      <template v-else>
        <main-loader />
      </template>
    </template>
  </main-large-dialog>
</template>

<script>
import { get, call } from 'vuex-pathify';

import MainLoader from '@/components/MainLoader';
import DataInput from '@/components/DataInput';
import MainLargeDialog from '@/components/MainLargeDialog';

export default {
  name: 'DataSourceAttributesEditing',
  components: {
    MainDialog: () => import('@/components/MainDialog'),
    MainLargeDialog,
    MainLoader,
    DataInput,
  },
  props: {
    editingDataSource: {
      type: Object,
      required: true,
    },
    isVisible: {
      type: Boolean,
      required: false,
    },
    isDataSourceFromLayer: {
      type: Boolean,
      required: false,
    },
  },
  data: vm => ({
    dataType: {
      name: 'select',
    },
    dataTypeText: {
      name: 'text',
    },
    attributeToDelete: {},
    isDeleteAttributeVisible: false,
    isDeleteAttributeLoaded: false,
    isLoaded: false,
    isNewAttributeValid: false,
    newAttribute: {},
    buttons: [
      {
        key: 'cancel',
        label: vm.$i18n.t('dialog.disagreeClose'),
        cancel: true,
      },
    ],
    columnsTitles: [
      {
        name: 'verbose_name',
        translatePath: 'dialog.body.displayName',
      },
      {
        name: 'name',
        translatePath: 'dialog.systemName',
        info: true,
      },
      {
        name: 'dataType',
        translatePath: 'dialog.dataType',
        info: true,
      },
      {
        name: 'desc_attribute_name',
        translatePath: 'dialog.descriptionName',
        info: true,
      },
      {
        name: 'dict',
        translatePath: 'dialog.dictionary',
        info: true,
      },
      {
        name: 'units',
        translatePath: 'dialog.units',
        info: true,
      },
      {
        name: 'delete',
        translatePath: 'dialog.deleteAttributes',
      },
    ],
  }),
  computed: {
    computedDataSource: {
      get() {
        return this.editingDataSource;
      },
      set(nV) {
        this.$emit('update:editingDataSource', nV);
      },
    },
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    dictionaries: get('admin/dictionaries'),
    dataTypes: get('layers/dataTypes'),
    datasources: get('layers/metadata'),
    measurementUnits: get('layers/measurementUnits'),
    formattedMeasurementUnits() {
      let formattedMeasurementUnits = [];
      for (let measurementUnitGroup of Object.keys(this.measurementUnits)) {
        formattedMeasurementUnits.push({ header: measurementUnitGroup });
        formattedMeasurementUnits = [
          ...formattedMeasurementUnits,
          ...this.measurementUnits[measurementUnitGroup].map(mU => {
            return { id: mU.id, text: `${mU.name} [${mU.symbol}]` };
          }),
        ];
        formattedMeasurementUnits.push({ divider: true });
      }
      return formattedMeasurementUnits;
    },
    excludedDataTypes() {
      return this.dataTypes.filter(d => d.name !== 'list' && d.name !== 'geometric');
    },
    filteredAttributes() {
      // TODO topogeom chyba powinno mieć data_type.name === 'geometric'
      // tak jak zwykła geometria, bo odfiltrowywanie po nazwie to 🤮
      const filteredAttributes = this.computedDataSource.attributes_schema.attributes
        .filter(attribute => attribute.data_type.name !== 'geometric' && attribute.name !== 'topogeom')
        .sort((a, b) => a.verbose_name.localeCompare(b.verbose_name));
      return JSON.parse(JSON.stringify(filteredAttributes));
    },
    descAttribute: {
      get() {
        return this.computedDataSource.attributes_schema.desc_attribute_name;
      },
      set(nV) {
        this.setDescAttribute(nV);
      },
    },
  },
  methods: {
    getDicts: call('admin/getDictionaries'),
    getDataTypes: call('layers/getDataTypes'),
    getMeasurementUnits: call('layers/getMeasurementUnits'),
    addAttribute: call('layers/addAttribute'),
    deleteAttribute: call('layers/deleteAttribute'),
    setAttribute: call('layers/setAttribute'),
    editDataSource: call('layers/editDataSourceMetadata'),
    getLayerMetadata: call('layers/getLayerMetadata'),
    closeModal() {
      this.isAttributesEditingVisible = false;
    },
    async setDescAttribute(attr) {
      const payload = {
        name: this.editingDataSource.name,
        body: {
          desc_attribute_name: attr,
          verbose_name: this.editingDataSource.verbose_name,
        },
      };
      try {
        await this.editDataSource(payload);
        await this.getLayerMetadata({ dataSource: this.editingDataSource.name });
      } catch (error) {
        console.log('error:', error);
      }
    },
    async setAttributeHandler(value, attribute, type) {
      value = value ? value : null;
      const payload = {
        attributeName: attribute.name,
        dataSourceName: this.computedDataSource.name,
        type,
        value,
      };
      try {
        const r = await this.setAttribute(payload);
        this.computedDataSource.attributes_schema.attributes.map(a => {
          return a.name === attribute.name ? r.data.data : a;
        });
        this.$root.$emit('attributesChanged');
      } catch {
        this.handleSetAttributeError(attribute.name, type);
      }
    },
    handleSetAttributeError(attributeName, type) {
      const errorAttribute = this.computedDataSource.attributes_schema.attributes.find(aT => aT.name === attributeName);
      const errorAttributeIdx = this.computedDataSource.attributes_schema.attributes.indexOf(errorAttribute);
      const key = type === 'dict_id' ? 'dict_id' : 'measurement_unit';
      const properValue = this.datasources[this.computedDataSource.name].attributes_schema.attributes.find(
        aT => aT.name === attributeName
      )[key];
      this.computedDataSource.attributes_schema.attributes[errorAttributeIdx][key] = properValue;
    },
    async handleAttributeAdd() {
      if (!this.isNewAttributeValid) {
        return;
      } else if (!this.newAttribute.name) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.attributeNameRequired'),
        });
      } else if (this.newAttribute.name.includes(' ')) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.attributeNameContainSpaces'),
        });
      } else if (!this.newAttribute.data_type) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.attributeTypeRequired'),
        });
      } else {
        if (!this.newAttribute.dict_id) {
          this.newAttribute.dict_id = null;
        }
        const payload = {
          data: this.newAttribute,
          dataSourceName: this.computedDataSource.name,
        };
        const r = await this.addAttribute(payload);
        if (!this.isDataSourceFromLayer) {
          this.computedDataSource.attributes_schema.attributes.push(r.data.data);
        }
        this.$root.$emit('attributesChanged');
        this.newAttribute = {};
        this.$refs?.form?.resetValidation();
      }
    },
    confirmAttributeDelete(attribute) {
      this.attributeToDelete = attribute;
      this.isDeleteAttributeLoaded = true;
      this.isDeleteAttributeVisible = true;
    },
    async handleAttributeDelete() {
      const attributeName = this.attributeToDelete.name;
      const payload = {
        attributeName,
        dataSourceName: this.computedDataSource.name,
      };
      await this.deleteAttribute(payload);
      this.$root.$emit('attributesChanged');
      const attributes = JSON.parse(JSON.stringify(this.computedDataSource.attributes_schema.attributes));
      const computedDataSource = JSON.parse(JSON.stringify(this.computedDataSource));
      computedDataSource.attributes_schema.attributes = attributes.filter(a => a.name !== attributeName);
      this.computedDataSource = computedDataSource;
      this.isDeleteAttributeVisible = false;
    },
    async init() {
      await Promise.all([this.getDicts(), this.getDataTypes(), this.getMeasurementUnits()]);
      this.isLoaded = true;
    },
  },
  watch: {
    isAttributesEditingVisible(nv) {
      if (!nv) {
        this.$emit('update:isAttributesEditingVisible', false);
      }
    },
  },
  created() {
    this.init();
  },
};
</script>

<style lang="scss" scoped>
.columnTitle {
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 13px;
  line-height: 16px;
}
.columnElementText {
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 16px;
  overflow: hidden;
  text-align: center;
}
.border-top {
  @include border(top, 1px);
}
::v-deep {
  .v-input {
    font-size: 12px;
  }
  .v-text-field.v-text-field--enclosed .v-text-field__details {
    margin-bottom: 0;
  }
}
</style>
